import React, { useState, useEffect, useCallback } from "react";
import QMSLayout from "../components/QMSLayout";
import { useAuth } from "../App";
import { useTranslation } from "react-i18next";
import { formatDateTime, formatRelativeTime } from "../utils/dateFormat";
import {
  Brain, RefreshCw, ChevronDown, ChevronRight, Loader2,
  ShieldCheck, ShieldAlert, AlertTriangle, CheckCircle2, XCircle,
  Clock, Zap, Target, TrendingUp, Activity, FileText,
  ThumbsUp, ThumbsDown, Timer, ArrowRight, Sparkles, Eye,
  Server, Bot, BarChart3, ListChecks
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ==================== CONSTANTS ====================

const BAND_STYLES = {
  "Excellent":         { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/30", ring: "ring-emerald-500/20" },
  "Good":              { bg: "bg-green-500/10", text: "text-green-400", border: "border-green-500/30", ring: "ring-green-500/20" },
  "Audit Ready":       { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/30", ring: "ring-emerald-500/20" },
  "Nearly Ready":      { bg: "bg-green-500/10", text: "text-green-400", border: "border-green-500/30", ring: "ring-green-500/20" },
  "Strong":            { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/30", ring: "ring-emerald-500/20" },
  "Adequate":          { bg: "bg-green-500/10", text: "text-green-400", border: "border-green-500/30", ring: "ring-green-500/20" },
  "Low Risk":          { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/30", ring: "ring-emerald-500/20" },
  "Moderate Risk":     { bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/30", ring: "ring-amber-500/20" },
  "Needs Attention":   { bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/30", ring: "ring-amber-500/20" },
  "Gaps Exist":        { bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/30", ring: "ring-amber-500/20" },
  "Partial":           { bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/30", ring: "ring-amber-500/20" },
  "At Risk":           { bg: "bg-orange-500/10", text: "text-orange-400", border: "border-orange-500/30", ring: "ring-orange-500/20" },
  "Significant Gaps":  { bg: "bg-orange-500/10", text: "text-orange-400", border: "border-orange-500/30", ring: "ring-orange-500/20" },
  "Weak":              { bg: "bg-orange-500/10", text: "text-orange-400", border: "border-orange-500/30", ring: "ring-orange-500/20" },
  "High Risk":         { bg: "bg-red-500/10", text: "text-red-400", border: "border-red-500/30", ring: "ring-red-500/20" },
  "Critical":          { bg: "bg-red-500/10", text: "text-red-400", border: "border-red-500/30", ring: "ring-red-500/20" },
  "Not Ready":         { bg: "bg-red-500/10", text: "text-red-400", border: "border-red-500/30", ring: "ring-red-500/20" },
  "Non-Compliant":     { bg: "bg-red-500/10", text: "text-red-400", border: "border-red-500/30", ring: "ring-red-500/20" },
};

const PRIORITY_STYLES = {
  1: { bg: "bg-red-500/15", text: "text-red-400", label: "P1 — Critical" },
  2: { bg: "bg-amber-500/15", text: "text-amber-400", label: "P2 — Warning" },
  3: { bg: "bg-sky-500/15", text: "text-sky-400", label: "P3 — Optimize" },
};

const VERDICT_STYLES = {
  pass:        { bg: "bg-emerald-500/10", text: "text-emerald-400", icon: CheckCircle2, label: "Verified" },
  conditional: { bg: "bg-amber-500/10", text: "text-amber-400", icon: AlertTriangle, label: "Conditional" },
  fail:        { bg: "bg-red-500/10", text: "text-red-400", icon: XCircle, label: "Failed" },
  pending:     { bg: "bg-[#2D313D]", text: "text-[#8B95A5]", icon: Clock, label: "Pending" },
};

const STATUS_STYLES = {
  suggested:  { bg: "bg-sky-500/10", text: "text-sky-400" },
  accepted:   { bg: "bg-emerald-500/10", text: "text-emerald-400" },
  dismissed:  { bg: "bg-[#2D313D]", text: "text-[#8B95A5]" },
  deferred:   { bg: "bg-amber-500/10", text: "text-amber-400" },
  applied:    { bg: "bg-emerald-500/10", text: "text-emerald-400" },
  superseded: { bg: "bg-[#2D313D]", text: "text-[#666]" },
  stale:      { bg: "bg-[#2D313D]", text: "text-[#555]" },
};

function getBandStyle(band) {
  return BAND_STYLES[band] || { bg: "bg-[#2D313D]", text: "text-[#8B95A5]", border: "border-[#2D313D]", ring: "ring-[#2D313D]" };
}

// ==================== SCORE ARC COMPONENT ====================

function ScoreArc({ value, max = 100, band, size = 80 }) {
  const pct = Math.min(value / max, 1);
  const radius = (size - 8) / 2;
  const circumference = Math.PI * radius;
  const offset = circumference * (1 - pct);
  const style = getBandStyle(band);

  const colorMap = {
    "text-emerald-400": "#34D399",
    "text-green-400": "#4ADE80",
    "text-amber-400": "#FBBF24",
    "text-orange-400": "#FB923C",
    "text-red-400": "#F87171",
    "text-sky-400": "#38BDF8",
    "text-[#8B95A5]": "#8B95A5",
  };
  const strokeColor = colorMap[style.text] || "#8B95A5";

  return (
    <svg width={size} height={size / 2 + 8} viewBox={`0 0 ${size} ${size / 2 + 8}`}>
      <path
        d={`M 4 ${size / 2 + 4} A ${radius} ${radius} 0 0 1 ${size - 4} ${size / 2 + 4}`}
        fill="none" stroke="#1E2230" strokeWidth="6" strokeLinecap="round"
      />
      <path
        d={`M 4 ${size / 2 + 4} A ${radius} ${radius} 0 0 1 ${size - 4} ${size / 2 + 4}`}
        fill="none" stroke={strokeColor} strokeWidth="6" strokeLinecap="round"
        strokeDasharray={circumference} strokeDashoffset={offset}
        style={{ transition: "stroke-dashoffset 0.8s ease-out" }}
      />
      <text x={size / 2} y={size / 2} textAnchor="middle" fill="white" fontSize="18" fontWeight="700">{value}</text>
      <text x={size / 2} y={size / 2 + 14} textAnchor="middle" fill="#8B95A5" fontSize="9">{band}</text>
    </svg>
  );
}

// ==================== SCORECARD CARD ====================

function ScorecardCard({ sc, t }) {
  const [open, setOpen] = useState(false);
  const style = getBandStyle(sc.score_band);
  const title = (sc.score_type || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div className={`rounded-lg border ${style.border} ${style.bg} p-4 transition-all`} data-testid={`scorecard-${sc.score_type}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate">{title}</p>
          <p className={`text-[10px] mt-0.5 ${style.text} uppercase tracking-wide font-medium`}>{sc.score_band}</p>
        </div>
        <ScoreArc value={sc.score_value} band={sc.score_band} size={72} />
      </div>
      {sc.components && (
        <div className="mt-3">
          <button
            className="flex items-center gap-1 text-[11px] text-[#8B95A5] hover:text-white transition-colors"
            onClick={() => setOpen(!open)}
            data-testid={`scorecard-${sc.score_type}-toggle`}
          >
            {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            {t('predictive.components')}
          </button>
          {open && (
            <div className="mt-2 space-y-1.5">
              {Object.entries(sc.components).map(([key, data]) => {
                const val = typeof data === "object" ? data.value : data;
                const max = typeof data === "object" ? data.max : null;
                return (
                  <div key={key} className="flex items-center justify-between gap-2">
                    <span className="text-[11px] text-[#8B95A5] truncate">{key.replace(/_/g, " ")}</span>
                    <div className="flex items-center gap-1.5">
                      <div className="w-16 h-1 rounded-full bg-[#1E2230] overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${max ? (val / max) * 100 : val}%`,
                            background: max ? (val / max > 0.6 ? "#4ADE80" : val / max > 0.3 ? "#FBBF24" : "#F87171") : "#38BDF8",
                          }}
                        />
                      </div>
                      <span className="text-[10px] text-white font-mono w-10 text-right">
                        {val}{max ? `/${max}` : ""}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {sc.created_at && (
        <p className="text-[10px] text-[#555] mt-2">{formatRelativeTime(sc.created_at)}</p>
      )}
    </div>
  );
}

// ==================== RECOMMENDATION CARD ====================

function RecommendationCard({ rec, onFeedback, feedbackLoading }) {
  const ps = PRIORITY_STYLES[rec.priority] || PRIORITY_STYLES[3];
  const ss = STATUS_STYLES[rec.status] || STATUS_STYLES.suggested;
  const isActive = rec.status === "suggested";

  return (
    <div className="rounded-lg border border-[rgba(255,255,255,0.06)] bg-[#14161C] p-3.5" data-testid={`rec-${rec.rec_id}`}>
      <div className="flex items-start gap-2 mb-2">
        <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${ps.bg} ${ps.text}`}>{ps.label}</span>
        <span className={`text-[9px] px-1.5 py-0.5 rounded ${ss.bg} ${ss.text}`}>{rec.status}</span>
      </div>
      <p className="text-[13px] text-white font-medium leading-snug">{rec.title || rec.rec_type?.replace(/_/g, " ")}</p>
      {rec.suggested_action && (
        <p className="text-[11px] text-[#8B95A5] mt-1.5 leading-relaxed">{rec.suggested_action}</p>
      )}
      {isActive && (
        <div className="flex items-center gap-1.5 mt-3" data-testid={`rec-actions-${rec.rec_id}`}>
          {["accepted", "dismissed", "deferred", "applied"].map(decision => {
            const icons = { accepted: ThumbsUp, dismissed: ThumbsDown, deferred: Timer, applied: CheckCircle2 };
            const colors = { accepted: "text-emerald-400 hover:bg-emerald-500/10", dismissed: "text-[#8B95A5] hover:bg-[#2D313D]", deferred: "text-amber-400 hover:bg-amber-500/10", applied: "text-sky-400 hover:bg-sky-500/10" };
            const Icon = icons[decision];
            return (
              <button
                key={decision}
                className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-[rgba(255,255,255,0.06)] ${colors[decision]} transition-all disabled:opacity-30`}
                onClick={() => onFeedback(rec.rec_id, decision)}
                disabled={feedbackLoading}
                data-testid={`rec-${rec.rec_id}-${decision}`}
                title={decision}
              >
                <Icon size={11} />
                {decision.charAt(0).toUpperCase() + decision.slice(1)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ==================== EXPLANATION PANEL ====================

function ExplanationPanel({ explanation, onVerify, verifyLoading, t }) {
  if (!explanation) return null;
  const vs = explanation.verification_status || "pending";
  const verdict = VERDICT_STYLES[vs] || VERDICT_STYLES.pending;
  const VIcon = verdict.icon;
  const content = explanation.explanation_content || {};

  return (
    <div className="rounded-lg border border-[rgba(255,255,255,0.06)] bg-[#14161C] p-4" data-testid="explanation-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-[#E8A838]" />
          <span className="text-sm font-semibold text-white">{t('predictive.aiExplanation')}</span>
          <span className="text-[9px] text-[#555] font-mono">{explanation.model_name}</span>
        </div>
        <div className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded ${verdict.bg} ${verdict.text}`} data-testid="verification-badge">
          <VIcon size={11} />
          {verdict.label}
        </div>
      </div>

      {content.summary && (
        <p className="text-[13px] text-[#CBD5E1] leading-relaxed mb-3">{content.summary}</p>
      )}

      {content.key_findings?.length > 0 && (
        <div className="mb-3">
          <p className="text-[11px] text-[#8B95A5] uppercase tracking-wide font-medium mb-1.5">Key Findings</p>
          <ul className="space-y-1">
            {content.key_findings.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-[12px] text-[#CBD5E1]">
                <Target size={10} className="text-[#38BDF8] mt-1 shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {content.risk_areas?.length > 0 && (
        <div className="mb-3">
          <p className="text-[11px] text-[#8B95A5] uppercase tracking-wide font-medium mb-1.5">{t('predictive.riskAreas')}</p>
          <ul className="space-y-1">
            {content.risk_areas.map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-[12px] text-orange-300">
                <AlertTriangle size={10} className="text-orange-400 mt-1 shrink-0" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {content.suggested_focus && (
        <div className="mb-3 p-2.5 rounded bg-[#0D0F14] border border-[rgba(255,255,255,0.04)]">
          <p className="text-[11px] text-[#8B95A5] uppercase tracking-wide font-medium mb-1">{t('predictive.suggestedFocus')}</p>
          <p className="text-[12px] text-sky-300">{content.suggested_focus}</p>
        </div>
      )}

      {content.confidence_note && (
        <p className="text-[10px] text-[#555] italic">{content.confidence_note}</p>
      )}

      {/* Verification Details */}
      {explanation.verification_result && (
        <div className="mt-3 pt-3 border-t border-[rgba(255,255,255,0.04)]" data-testid="verification-details">
          <p className="text-[11px] text-[#8B95A5] uppercase tracking-wide font-medium mb-2">{t('predictive.verificationChecks')}</p>
          <div className="grid grid-cols-2 gap-2">
            {["safety_check", "clarity_check", "consistency_check", "autonomy_check"].map(check => {
              const c = explanation.verification_result[check];
              if (!c) return null;
              return (
                <div key={check} className={`p-2 rounded text-[11px] ${c.pass ? "bg-emerald-500/5 border border-emerald-500/20" : "bg-red-500/5 border border-red-500/20"}`}>
                  <div className="flex items-center gap-1 mb-0.5">
                    {c.pass ? <CheckCircle2 size={10} className="text-emerald-400" /> : <XCircle size={10} className="text-red-400" />}
                    <span className="text-white font-medium">{check.replace("_check", "").replace(/^\w/, c => c.toUpperCase())}</span>
                  </div>
                  {c.notes && <p className="text-[#8B95A5] text-[10px]">{c.notes}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {vs === "pending" && (
        <button
          className="mt-3 flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded bg-[#E8A838]/10 text-[#E8A838] border border-[#E8A838]/20 hover:bg-[#E8A838]/20 transition-all disabled:opacity-40"
          onClick={() => onVerify(explanation.explanation_id)}
          disabled={verifyLoading}
          data-testid="verify-explanation-btn"
        >
          {verifyLoading ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
          {t('predictive.verifyWithClaude')}
        </button>
      )}

      <p className="text-[10px] text-[#555] mt-2">{formatRelativeTime(explanation.created_at)}</p>
    </div>
  );
}

// ==================== MODEL RUN LOG TABLE ====================

function ModelRunTable({ runs }) {
  if (!runs?.length) return <p className="text-[11px] text-[#555] py-4 text-center">No model runs recorded</p>;
  return (
    <div className="overflow-x-auto" data-testid="model-run-table">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-[#8B95A5] border-b border-[rgba(255,255,255,0.06)]">
            <th className="text-left py-2 px-2 font-medium">Run ID</th>
            <th className="text-left py-2 px-2 font-medium">Model</th>
            <th className="text-left py-2 px-2 font-medium">Purpose</th>
            <th className="text-right py-2 px-2 font-medium">Latency</th>
            <th className="text-left py-2 px-2 font-medium">Status</th>
            <th className="text-left py-2 px-2 font-medium">Time</th>
          </tr>
        </thead>
        <tbody>
          {runs.map(r => (
            <tr key={r.run_id} className="border-b border-[rgba(255,255,255,0.03)] hover:bg-[rgba(255,255,255,0.02)]">
              <td className="py-1.5 px-2 font-mono text-[#8B95A5]">{r.run_id?.slice(0, 14)}</td>
              <td className="py-1.5 px-2 text-white">{r.model_name}</td>
              <td className="py-1.5 px-2 text-[#CBD5E1]">{r.purpose?.replace(/_/g, " ")}</td>
              <td className="py-1.5 px-2 text-right font-mono text-[#8B95A5]">{r.latency_ms ? `${r.latency_ms}ms` : "-"}</td>
              <td className="py-1.5 px-2">
                <span className={`text-[9px] px-1.5 py-0.5 rounded ${r.status === "success" ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"}`}>{r.status}</span>
              </td>
              <td className="py-1.5 px-2 text-[#555]">{formatRelativeTime(r.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ==================== FEEDBACK HISTORY TABLE ====================

function FeedbackTable({ entries, t }) {
  if (!entries?.length) return <p className="text-[11px] text-[#555] py-4 text-center">{t('predictive.noFeedback')}</p>;
  return (
    <div className="overflow-x-auto" data-testid="feedback-history-table">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-[#8B95A5] border-b border-[rgba(255,255,255,0.06)]">
            <th className="text-left py-2 px-2 font-medium">{t('predictive.recId')}</th>
            <th className="text-left py-2 px-2 font-medium">{t('predictive.decision')}</th>
            <th className="text-left py-2 px-2 font-medium">{t('predictive.reason')}</th>
            <th className="text-left py-2 px-2 font-medium">{t('predictive.actor')}</th>
            <th className="text-left py-2 px-2 font-medium">{t('predictive.time')}</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e, i) => (
            <tr key={i} className="border-b border-[rgba(255,255,255,0.03)] hover:bg-[rgba(255,255,255,0.02)]">
              <td className="py-1.5 px-2 font-mono text-[#8B95A5]">{e.rec_id?.slice(0, 14)}</td>
              <td className="py-1.5 px-2">
                <span className={`text-[9px] px-1.5 py-0.5 rounded ${STATUS_STYLES[e.decision]?.bg || "bg-[#2D313D]"} ${STATUS_STYLES[e.decision]?.text || "text-[#8B95A5]"}`}>{e.decision}</span>
              </td>
              <td className="py-1.5 px-2 text-[#CBD5E1] max-w-[200px] truncate">{e.reason || "-"}</td>
              <td className="py-1.5 px-2 text-[#8B95A5]">{e.actor_name || e.actor_id}</td>
              <td className="py-1.5 px-2 text-[#555]">{formatRelativeTime(e.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ==================== SIGNALS PANEL ====================

function SignalsPanel({ signals }) {
  const { t } = useTranslation('platform');
  const [open, setOpen] = useState(false);
  if (!signals?.length) return null;

  const severityOrder = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
  const sorted = [...signals].sort((a, b) => (severityOrder[a.severity] ?? 5) - (severityOrder[b.severity] ?? 5));

  return (
    <div className="rounded-lg border border-[rgba(255,255,255,0.06)] bg-[#14161C] p-3.5" data-testid="signals-panel">
      <button className="flex items-center gap-2 w-full" onClick={() => setOpen(!open)}>
        <Activity size={14} className="text-[#38BDF8]" />
        <span className="text-sm font-medium text-white">{t('predictive.contributingSignals')}</span>
        <span className="text-[10px] text-[#8B95A5] ml-1">({signals.length})</span>
        <span className="ml-auto">{open ? <ChevronDown size={12} className="text-[#8B95A5]" /> : <ChevronRight size={12} className="text-[#8B95A5]" />}</span>
      </button>
      {open && (
        <div className="mt-2.5 space-y-1.5 max-h-[300px] overflow-y-auto">
          {sorted.map((s, i) => {
            const sevColor = { critical: "text-red-400", high: "text-orange-400", medium: "text-amber-400", low: "text-green-400", info: "text-sky-400" };
            return (
              <div key={i} className="flex items-start gap-2 p-2 rounded bg-[#0D0F14] border border-[rgba(255,255,255,0.03)]">
                <Zap size={10} className={`${sevColor[s.severity] || "text-[#8B95A5]"} mt-0.5 shrink-0`} />
                <div className="flex-1 min-w-0">
                  <span className="text-[11px] text-white">{s.signal_type?.replace(/_/g, " ")}</span>
                  <span className={`text-[9px] ml-1.5 ${sevColor[s.severity] || "text-[#8B95A5]"}`}>({s.severity})</span>
                  {s.value != null && <p className="text-[10px] text-[#8B95A5] truncate">{String(s.value)}</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ==================== MAIN PAGE ====================

export default function PredictiveIntelligencePage() {
  const { user } = useAuth();
  const { t } = useTranslation('platform');

  // State
  const [entityType, setEntityType] = useState("organization");
  const [entityId, setEntityId] = useState("");
  const [orgList, setOrgList] = useState([]);
  const [scorecards, setScorecards] = useState([]);
  const [signals, setSignals] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [explanation, setExplanation] = useState(null);
  const [modelRuns, setModelRuns] = useState([]);
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState({});
  const [bottomTab, setBottomTab] = useState("model-runs");

  const setLoadingKey = (key, val) => setLoading(p => ({ ...p, [key]: val }));

  // Fetch org list on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/crm/organizations`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          const orgs = data.organizations || data || [];
          setOrgList(Array.isArray(orgs) ? orgs : []);
          if (orgs.length > 0) setEntityId(orgs[0].org_id || "");
        }
      } catch {}
    })();
  }, []);

  // Run full analysis
  const runAnalysis = useCallback(async () => {
    if (entityType === "organization" && !entityId) return;
    setLoadingKey("analysis", true);
    setScorecards([]);
    setSignals([]);
    setRecommendations([]);
    setExplanation(null);

    try {
      // 1. Run scorecards
      const scBody = entityId ? { score_type: "all", entity_id: entityId } : { score_type: "all" };
      const scRes = await fetch(`${API}/api/predictive/scorecards/run`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(scBody),
      });
      if (scRes.ok) {
        const scData = await scRes.json();
        setScorecards(scData.scorecards || []);
      }

      // 2. Collect signals
      const sigEntityType = entityId ? "organization" : "platform";
      const sigEntityId = entityId || "global";
      const sigRes = await fetch(`${API}/api/predictive/signals/${sigEntityType}/${sigEntityId}`, { credentials: "include" });
      if (sigRes.ok) {
        const sigData = await sigRes.json();
        setSignals(sigData.signals || []);
      }

      // 3. Load existing recommendations
      const recRes = await fetch(`${API}/api/predictive/recommendations/${sigEntityType}/${sigEntityId}`, { credentials: "include" });
      if (recRes.ok) {
        const recData = await recRes.json();
        setRecommendations(recData.recommendations || []);
      }

      // 4. Load latest explanation
      const expRes = await fetch(`${API}/api/predictive/explanations/${sigEntityType}/${sigEntityId}?limit=1`, { credentials: "include" });
      if (expRes.ok) {
        const expData = await expRes.json();
        setExplanation(expData.explanations?.[0] || null);
      }

      setLastUpdated(new Date().toISOString());
    } catch (err) {
      console.error("Analysis error:", err);
    } finally {
      setLoadingKey("analysis", false);
    }
  }, [entityType, entityId]);

  // Auto-run on entity change
  useEffect(() => {
    if (entityId || entityType === "platform") runAnalysis();
  }, [entityId, entityType, runAnalysis]);

  // Load bottom tab data
  useEffect(() => {
    (async () => {
      if (bottomTab === "model-runs") {
        try {
          const res = await fetch(`${API}/api/predictive/model-runs?limit=50`, { credentials: "include" });
          if (res.ok) { const d = await res.json(); setModelRuns(d.runs || []); }
        } catch {}
      } else if (bottomTab === "feedback") {
        const eType = entityId ? "organization" : "platform";
        const eId = entityId || "global";
        try {
          const res = await fetch(`${API}/api/predictive/feedback/history?entity_type=${eType}&entity_id=${eId}&limit=50`, { credentials: "include" });
          if (res.ok) { const d = await res.json(); setFeedbackHistory(d.history || []); }
        } catch {}
      }
    })();
  }, [bottomTab, entityId]);

  // Generate recommendations
  const generateRecs = async () => {
    setLoadingKey("recs", true);
    try {
      const body = entityId ? { entity_id: entityId, entity_type: "organization" } : { entity_type: "platform" };
      const res = await fetch(`${API}/api/predictive/recommendations/generate`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const d = await res.json();
        setRecommendations(d.recommendations || []);
      }
    } catch {}
    setLoadingKey("recs", false);
  };

  // Generate explanation
  const generateExplanation = async () => {
    setLoadingKey("explanation", true);
    try {
      const body = entityId ? { entity_id: entityId, entity_type: "organization" } : { entity_type: "platform" };
      const res = await fetch(`${API}/api/predictive/explanations/generate`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const d = await res.json();
        setExplanation(d);
      }
    } catch {}
    setLoadingKey("explanation", false);
  };

  // Verify explanation
  const verifyExplanation = async (expId) => {
    setLoadingKey("verify", true);
    try {
      const res = await fetch(`${API}/api/predictive/explanations/${expId}/verify`, {
        method: "POST", credentials: "include",
      });
      if (res.ok) {
        const d = await res.json();
        setExplanation(prev => prev ? {
          ...prev,
          verification_status: d.verification_result?.verdict || "pending",
          verification_result: d.verification_result,
        } : prev);
      }
    } catch {}
    setLoadingKey("verify", false);
  };

  // Submit feedback
  const submitFeedback = async (recId, decision) => {
    setLoadingKey("feedback", true);
    try {
      const res = await fetch(`${API}/api/predictive/recommendations/${recId}/feedback`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision, reason: "" }),
      });
      if (res.ok) {
        setRecommendations(prev => prev.map(r => r.rec_id === recId ? { ...r, status: decision } : r));
      }
    } catch {}
    setLoadingKey("feedback", false);
  };

  const activeRecs = recommendations.filter(r => r.status === "suggested");
  const resolvedRecs = recommendations.filter(r => r.status !== "suggested");

  return (
    <QMSLayout title={t('predictive.title')}>
      <div className="space-y-5 max-w-[1600px] mx-auto" data-testid="predictive-intelligence-page">
        {/* ==================== ZONE A: CONTEXT HEADER ==================== */}
        <div className="rounded-lg border border-[rgba(255,255,255,0.06)] bg-[#14161C] p-4" data-testid="predictive-header">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#E8A838]/20 to-[#E8A838]/5 flex items-center justify-center">
                <Brain size={20} className="text-[#E8A838]" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white tracking-tight">{t('predictive.title')}</h1>
                <p className="text-[11px] text-[#8B95A5]">{t('predictive.subtitle')}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {/* Entity Type Toggle */}
              <div className="flex items-center rounded bg-[#0D0F14] border border-[rgba(255,255,255,0.06)] overflow-hidden" data-testid="entity-type-toggle">
                <button
                  className={`text-[11px] px-3 py-1.5 transition-all ${entityType === "organization" ? "bg-[#E8A838]/15 text-[#E8A838]" : "text-[#8B95A5] hover:text-white"}`}
                  onClick={() => setEntityType("organization")}
                >{t('predictive.organization')}</button>
                <button
                  className={`text-[11px] px-3 py-1.5 transition-all ${entityType === "platform" ? "bg-[#E8A838]/15 text-[#E8A838]" : "text-[#8B95A5] hover:text-white"}`}
                  onClick={() => { setEntityType("platform"); setEntityId(""); }}
                >{t('predictive.platform')}</button>
              </div>

              {/* Org Selector */}
              {entityType === "organization" && (
                <select
                  className="text-[11px] px-3 py-1.5 rounded bg-[#0D0F14] border border-[rgba(255,255,255,0.06)] text-white focus:outline-none focus:border-[#E8A838]/30 max-w-[220px]"
                  value={entityId}
                  onChange={e => setEntityId(e.target.value)}
                  data-testid="entity-selector"
                >
                  <option value="">{t('predictive.selectOrg')}</option>
                  {orgList.map(o => (
                    <option key={o.org_id} value={o.org_id}>{o.name || o.org_id}</option>
                  ))}
                </select>
              )}

              {/* Run Analysis */}
              <button
                className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded bg-[#E8A838]/10 text-[#E8A838] border border-[#E8A838]/20 hover:bg-[#E8A838]/20 transition-all disabled:opacity-40"
                onClick={runAnalysis}
                disabled={loading.analysis || (entityType === "organization" && !entityId)}
                data-testid="run-analysis-btn"
              >
                {loading.analysis ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                {t('predictive.runAnalysis')}
              </button>

              {lastUpdated && (
                <span className="text-[10px] text-[#555]">{t('predictive.updated')} {formatRelativeTime(lastUpdated)}</span>
              )}
            </div>
          </div>
        </div>

        {/* ==================== ZONE B: MAIN INTELLIGENCE AREA ==================== */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* LEFT COLUMN — Scorecards + Signals */}
          <div className="space-y-4" data-testid="scorecards-column">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 size={14} className="text-[#38BDF8]" />
              <span className="text-xs font-semibold text-[#8B95A5] uppercase tracking-wide">{t('predictive.scorecards')}</span>
            </div>

            {loading.analysis ? (
              <div className="flex items-center justify-center py-12"><Loader2 size={24} className="animate-spin text-[#E8A838]" /></div>
            ) : scorecards.length === 0 ? (
              <div className="rounded-lg border border-dashed border-[rgba(255,255,255,0.08)] p-8 text-center">
                <BarChart3 size={24} className="text-[#2D313D] mx-auto mb-2" />
                <p className="text-[12px] text-[#555]">{t('predictive.noScorecards')}</p>
                <p className="text-[10px] text-[#444] mt-1">{t('predictive.selectEntityHint')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {scorecards.map(sc => <ScorecardCard key={sc.scorecard_id || sc.score_type} sc={sc} t={t} />)}
              </div>
            )}

            <SignalsPanel signals={signals} />
          </div>

          {/* CENTER COLUMN — AI Explanation */}
          <div className="space-y-4" data-testid="explanation-column">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-[#E8A838]" />
                <span className="text-xs font-semibold text-[#8B95A5] uppercase tracking-wide">{t('predictive.aiExplanation')}</span>
              </div>
              <button
                className="flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-[#E8A838]/10 text-[#E8A838] hover:bg-[#E8A838]/20 transition-all disabled:opacity-40"
                onClick={generateExplanation}
                disabled={loading.explanation || scorecards.length === 0}
                data-testid="generate-explanation-btn"
              >
                {loading.explanation ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
                {t('predictive.generate')}
              </button>
            </div>

            {loading.explanation ? (
              <div className="flex flex-col items-center justify-center py-12 gap-2">
                <Loader2 size={24} className="animate-spin text-[#E8A838]" />
                <p className="text-[11px] text-[#8B95A5]">{t('predictive.generatingExplanation')}</p>
              </div>
            ) : explanation ? (
              <ExplanationPanel explanation={explanation} onVerify={verifyExplanation} verifyLoading={loading.verify} t={t} />
            ) : (
              <div className="rounded-lg border border-dashed border-[rgba(255,255,255,0.08)] p-8 text-center">
                <Sparkles size={24} className="text-[#2D313D] mx-auto mb-2" />
                <p className="text-[12px] text-[#555]">{t('predictive.noExplanation')}</p>
                <p className="text-[10px] text-[#444] mt-1">{t('predictive.runScorecardsFirst')}</p>
              </div>
            )}

            {/* Explanation metadata */}
            {explanation && (
              <div className="rounded bg-[#0D0F14] border border-[rgba(255,255,255,0.04)] p-3" data-testid="explanation-meta">
                <div className="flex items-center gap-2 text-[10px] text-[#555]">
                  <Bot size={10} />
                  <span>{t('predictive.model')}: {explanation.model_name} | ID: {explanation.explanation_id}</span>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT COLUMN — Recommendations */}
          <div className="space-y-4" data-testid="recommendations-column">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <ListChecks size={14} className="text-emerald-400" />
                <span className="text-xs font-semibold text-[#8B95A5] uppercase tracking-wide">{t('predictive.recommendations')}</span>
                {activeRecs.length > 0 && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#E8A838]/15 text-[#E8A838] font-bold">{activeRecs.length}</span>
                )}
              </div>
              <button
                className="flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-all disabled:opacity-40"
                onClick={generateRecs}
                disabled={loading.recs || scorecards.length === 0}
                data-testid="generate-recs-btn"
              >
                {loading.recs ? <Loader2 size={10} className="animate-spin" /> : <Zap size={10} />}
                {t('predictive.generate')}
              </button>
            </div>

            {loading.recs ? (
              <div className="flex items-center justify-center py-12"><Loader2 size={24} className="animate-spin text-emerald-400" /></div>
            ) : recommendations.length === 0 ? (
              <div className="rounded-lg border border-dashed border-[rgba(255,255,255,0.08)] p-8 text-center">
                <ListChecks size={24} className="text-[#2D313D] mx-auto mb-2" />
                <p className="text-[12px] text-[#555]">{t('predictive.noRecommendations')}</p>
                <p className="text-[10px] text-[#444] mt-1">{t('predictive.generateFromScorecard')}</p>
              </div>
            ) : (
              <div className="space-y-2.5 max-h-[600px] overflow-y-auto pr-1">
                {activeRecs.length > 0 && (
                  <>
                    <p className="text-[10px] text-[#8B95A5] uppercase tracking-wide font-medium">{t('predictive.actionRequired')} ({activeRecs.length})</p>
                    {activeRecs.map(r => <RecommendationCard key={r.rec_id} rec={r} onFeedback={submitFeedback} feedbackLoading={loading.feedback} />)}
                  </>
                )}
                {resolvedRecs.length > 0 && (
                  <>
                    <p className="text-[10px] text-[#555] uppercase tracking-wide font-medium mt-3">{t('predictive.resolved')} ({resolvedRecs.length})</p>
                    {resolvedRecs.slice(0, 5).map(r => <RecommendationCard key={r.rec_id} rec={r} onFeedback={submitFeedback} feedbackLoading={loading.feedback} />)}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ==================== ZONE C: BOTTOM SECTION ==================== */}
        <div className="rounded-lg border border-[rgba(255,255,255,0.06)] bg-[#14161C]" data-testid="bottom-section">
          {/* Tab bar */}
          <div className="flex items-center gap-0 border-b border-[rgba(255,255,255,0.06)] px-1">
            {[
              { id: "model-runs", label: t('predictive.modelRunAuditLog'), icon: Server },
              { id: "feedback", label: t('predictive.feedbackHistory'), icon: FileText },
            ].map(tab => (
              <button
                key={tab.id}
                className={`flex items-center gap-1.5 text-[11px] px-4 py-3 border-b-2 transition-all ${bottomTab === tab.id ? "border-[#E8A838] text-[#E8A838]" : "border-transparent text-[#8B95A5] hover:text-white"}`}
                onClick={() => setBottomTab(tab.id)}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon size={12} />
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-4">
            {bottomTab === "model-runs" && <ModelRunTable runs={modelRuns} t={t} />}
            {bottomTab === "feedback" && <FeedbackTable entries={feedbackHistory} t={t} />}
          </div>
        </div>
      </div>
    </QMSLayout>
  );
}

